/**
 * Incident filter catalog — the per-page filter "catalog" the spec refers to.
 * Each field: { id, label, kind, category, options?, apply(row, values) }.
 * Kinds: 'search' | 'single' | 'multi' | 'date-range'. (Free-text search + the Time-frame
 * date control are persistent bar controls, not in the add-filter picker.)
 *
 * EVERY field actually filters the table via apply(row, values): classification/Themis verdict,
 * threat type, resolved (auto/manual), detected-by (first-reporter kind), incident type, assignee,
 * and the Include/Exclude email properties (links, attachments, SOC agent, fake login, internal/
 * community comments, bubble ups, auto-Themis) — all backed by fields on the mock incidents.
 */

import { INCIDENT_TYPE } from 'containers/ResolutionCenter/constants'

// Longest-first so MSFT wins over MST. Mirrors backend
// extract_report_id_from_prefixed_search (webapp.utils.incident_tab_utils).
const PREFIXED_INCIDENT_ID_SEARCH = [
  { prefix: 'MSFT', reportTypes: [INCIDENT_TYPE.MSFT] },
  { prefix: 'ATO', reportTypes: [INCIDENT_TYPE.ATO] },
  { prefix: 'MST', reportTypes: [INCIDENT_TYPE.TEAMS] },
  {
    prefix: 'BUP',
    reportTypes: [INCIDENT_TYPE.POTENTIAL_MAIL, INCIDENT_TYPE.AUTO_THEMIS_POTENTIAL_MAIL],
  },
  { prefix: 'ENC', reportTypes: [INCIDENT_TYPE.ENCRYPTED_INCIDENT] },
  { prefix: 'IS', reportTypes: [INCIDENT_TYPE.MAIL, INCIDENT_TYPE.ENCRYPTED_INCIDENT] },
]
const PREFIXED_INCIDENT_ID_RE = /^(MSFT|ATO|MST|BUP|ENC|IS)[-\s]*(\d+)$/i
const REPORT_TYPES_BY_PREFIX = PREFIXED_INCIDENT_ID_SEARCH.reduce((acc, item) => {
  acc[item.prefix] = item.reportTypes
  return acc
}, {})

export const extractPrefixedIncidentSearch = (search) => {
  if (!search || !String(search).trim()) return null
  const match = PREFIXED_INCIDENT_ID_RE.exec(String(search).trim())
  if (!match) return null
  const prefix = match[1].toUpperCase()
  return { reportId: match[2], reportTypes: REPORT_TYPES_BY_PREFIX[prefix] }
}

const MONTH_IDX = {
  jan: 0,
  feb: 1,
  mar: 2,
  apr: 3,
  may: 4,
  jun: 5,
  jul: 6,
  aug: 7,
  sep: 8,
  oct: 9,
  nov: 10,
  dec: 11,
}

// Convert a mock `reported` display string to an approximate Date for time-frame filtering.
// Handles: 'Xh ago', 'Yesterday', 'D Month' (e.g. '26 May'), 'D Month YYYY'.
const parseReportedDate = (reported) => {
  if (!reported) return null
  const s = String(reported).trim()
  const now = new Date()
  const hm = /^(\d+)h ago$/i.exec(s)
  if (hm) {
    const d = new Date(now)
    d.setHours(d.getHours() - parseInt(hm[1], 10))
    return d
  }
  if (/^yesterday$/i.test(s)) {
    const d = new Date(now)
    d.setDate(d.getDate() - 1)
    return d
  }
  const dm = /^(\d{1,2})\s+([A-Za-z]+)(?:\s+(\d{4}))?$/.exec(s)
  if (dm) {
    const mi = MONTH_IDX[dm[2].toLowerCase().slice(0, 3)]
    if (mi !== undefined) {
      const yr = dm[3] ? parseInt(dm[3], 10) : now.getFullYear()
      return new Date(yr, mi, parseInt(dm[1], 10))
    }
  }
  return null
}

// Convert a TIME_PRESETS value to the earliest Date an incident can have and still match.
const timeFrameStart = (tf) => {
  const now = new Date()
  if (tf === '24h') return new Date(now.getTime() - 24 * 60 * 60 * 1000)
  if (tf === '3d') return new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000)
  if (tf === '7d') return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
  if (tf === '30d') return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
  return null
}

// Map an incident's first-reporter kind → the "Detected by" option label it corresponds to.
const DETECTION_BY_KIND = {
  user: 'End User',
  community: 'IRONSCALES Community',
  impersonation: 'Impersonation Protection',
  malware: 'Malware & URL Protection',
  automated: 'Automated Threat Detection',
  ai: 'Agentic Themis',
  soc: 'SOC Analyst',
}
// Multi-select semantics: no selection matches everything; otherwise value must be selected.
const inList = (value, values) => !values || !values.length || values.includes(value)

const yesNo = (cond, values) => {
  // single Include/Exclude semantics: values is ['Include'] | ['Exclude'] | []
  if (!values.length) return true
  return values[0] === 'Include' ? cond : !cond
}

export const TIME_PRESETS = [
  { value: '24h', label: 'Last 24 hours' },
  { value: '3d', label: 'Last 3 days' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: 'all', label: 'All time' },
  { value: 'custom', label: 'Custom…' },
]

/** Legacy `selectDateFiltersOptions`: POV_COSTUMER_FF hides the All-times preset. */
export const getTimePresets = ({ hideAllTimes = false } = {}) =>
  hideAllTimes ? TIME_PRESETS.filter((p) => p.value !== 'all') : TIME_PRESETS

const INCLUDE_OPTS = [
  { value: 'Include', label: 'Include' },
  { value: 'Exclude', label: 'Exclude' },
]

export const CATEGORIES = [
  {
    id: 'classification',
    label: 'Classification',
    fields: [
      {
        // State-aware: on the Unclassified tab this is "Themis verdict" (Themis's suggested verdict
        // pre-classification — Phishing / Spam / No threat detected). On Classified/Challenged it's
        // "Classification" (the applied verdict — Phishing / Spam / Safe). See resolveFieldForState.
        id: 'verdict',
        label: 'Themis verdict',
        kind: 'multi',
        options: [
          { value: 'Phishing', label: 'Phishing' },
          { value: 'Spam', label: 'Spam' },
          { value: 'no_threat', label: 'No threat detected' },
        ],
        altLabel: 'Classification',
        // Compromised is not shown in the dropdown — selecting Phishing still maps to phishing +
        // compromised quickFilters (see server.valueMap) so ATO remediated accounts are included.
        altOptions: [
          { value: 'Phishing', label: 'Phishing' },
          { value: 'Spam', label: 'Spam' },
          { value: 'Safe', label: 'Safe' },
        ],
        // Match either the applied classification (classified/challenged rows) or the Themis verdict
        // (unclassified rows), case-insensitively — so one apply() serves both filter variants.
        // ATO Compromised is the malicious outcome: it also matches the Phishing filter value.
        apply: (r, v) => {
          const raw = r.classification || (r.confidence && r.confidence.verdict) || ''
          const val = String(raw).toLowerCase()
          const selected = v.map((x) => String(x).toLowerCase())
          if (selected.includes(val)) return true
          if (val === 'compromised' && selected.includes('phishing')) return true
          return false
        },
        // Live: Classification chips → quickFilters (unified `state`) on Classified/Challenged.
        // On Unclassified, the same field maps to themisVerdictFilter — see filterMapping.js.
        // Phishing also sends Compromised (3) on classified so ATO remediated accounts match.
        server: {
          kind: 'quickFilters',
          valueMap: {
            Phishing: [1, 3], // UNIFIED_REPORT_STATE.PHISHING + COMPROMISED
            Spam: 2,
            Safe: 4,
            Compromised: 3,
          },
        },
      },
      {
        id: 'threatType',
        label: 'Threat type',
        kind: 'multi',
        options: [
          'BEC',
          'Credential Theft',
          'Display Name Impersonation',
          'Typo-Squatting',
          'Bulk Phishing',
          'Calendar Phishing',
          'Marketing',
          'Trusted Sender',
          'Malware',
        ].map((x) => ({ value: x, label: x })),
        apply: (r, v) => (r.confidence ? inList(r.confidence.threatType, v) : false),
        server: { kind: 'deferred', note: 'Not connected to live data yet' },
      },
      {
        id: 'resolved',
        label: 'Resolved',
        kind: 'multi',
        classifiedOnly: true,
        options: [
          { value: 'Automatically', label: 'Automatically' },
          { value: 'Manually', label: 'Manually' },
        ],
        apply: (r, v) =>
          inList(
            r.resolution === 'automatic'
              ? 'Automatically'
              : r.resolution === 'manual'
                ? 'Manually'
                : '',
            v
          ),
        server: { kind: 'resolvedFilter', valueMap: { Automatically: 1, Manually: 2 } },
      },
    ],
  },
  {
    id: 'detection',
    label: 'Detection',
    fields: [
      {
        id: 'detectedBy',
        label: 'Detected by',
        kind: 'multi',
        options: [
          'IRONSCALES Community',
          'Impersonation Protection',
          'End User',
          'Malware & URL Protection',
          'Automated Threat Detection',
          'Agentic Themis',
          'SOC Analyst',
        ].map((x) => ({ value: x, label: x })),
        apply: (r, v) => inList(DETECTION_BY_KIND[r.firstReporter && r.firstReporter.kind], v),
        server: {
          kind: 'detectedByFilter',
          valueMap: {
            'IRONSCALES Community': 1,
            'Impersonation Protection': 2,
            'End User': 3,
            'Malware & URL Protection': 4,
            'Automated Threat Detection': 5,
            'SOC Analyst': 6,
            // Not a reporter group: the AI-Bubble Up agent's incidents (report_consts.AGENTIC_THEMIS_FILTER_ID).
            'Agentic Themis': 7,
          },
        },
      },
    ],
  },
  {
    id: 'type',
    label: 'Incident type',
    fields: [
      {
        id: 'type',
        label: 'Incident type',
        kind: 'multi',
        // Unclassified / Classified: Email / ATO / Teams (quickFilters).
        options: [
          { value: 'email', label: 'Email' },
          { value: 'ato', label: 'Account Takeover' },
          { value: 'teams', label: 'Teams' },
        ],
        apply: (r, v) => inList(r.type, v),
        server: { kind: 'quickFilters', valueMap: { email: 5, ato: 6, teams: 7 } },
        // Challenged only (§4.2): IRONSCALES / MSFT → challengeSourceFilter.
        // See resolveFieldForState. Encrypted challenges are outside the domain and always kept
        // (mirrors get_challenge_source_filter_query).
        altOptions: [
          { value: 'ironscales', label: 'IRONSCALES' },
          { value: 'msft', label: 'MSFT' },
        ],
        altServer: {
          kind: 'challengeSourceFilter',
          valueMap: { ironscales: 1, msft: 2 },
        },
        altApply: (r, v) => {
          if (!v || !v.length) return true
          const map = {
            ironscales: INCIDENT_TYPE.MAIL,
            msft: INCIDENT_TYPE.MSFT,
          }
          const selected = v.map((x) => map[x]).filter((x) => x != null)
          if (!selected.length) return true
          const domain = [INCIDENT_TYPE.MAIL, INCIDENT_TYPE.MSFT]
          let rt = r.reportType
          if (rt == null) {
            if (r.isMicrosoft) rt = INCIDENT_TYPE.MSFT
            else if (r.isEncrypted) rt = INCIDENT_TYPE.ENCRYPTED_INCIDENT
            else rt = INCIDENT_TYPE.MAIL
          }
          return selected.includes(rt) || !domain.includes(rt)
        },
      },
    ],
  },
  {
    id: 'assignment',
    label: 'Assignment',
    fields: [
      {
        id: 'assignedTo',
        label: 'Assigned to',
        kind: 'multi',
        // Options come from the live assignees API (FieldPicker injects them). No mock names.
        options: [],
        apply: (r, v) => !!r.assignedTo && inList(r.assignedTo, v),
        server: { kind: 'assigneeFilter' },
      },
    ],
  },
  {
    id: 'properties',
    label: 'Email properties',
    fields: [
      {
        id: 'links',
        label: 'Links',
        kind: 'single',
        options: INCLUDE_OPTS,
        apply: (r, v) => yesNo(r.counts.links > 0, v),
        server: { kind: 'includeExclude', value: 1 },
      },
      {
        id: 'attachments',
        label: 'Attachments',
        kind: 'single',
        options: INCLUDE_OPTS,
        apply: (r, v) => yesNo(r.counts.attachments > 0, v),
        server: { kind: 'includeExclude', value: 3 },
      },
      {
        id: 'phishingSocAgent',
        label: 'Phishing SOC Agent',
        kind: 'single',
        options: INCLUDE_OPTS,
        apply: (r, v) => yesNo(!!r.hasSocAgent, v),
        server: { kind: 'includeExclude', value: 8 },
      },
      {
        id: 'fakeLogin',
        label: 'Fake Login',
        kind: 'single',
        options: INCLUDE_OPTS,
        apply: (r, v) => yesNo(!!r.hasFakeLogin, v),
        server: { kind: 'includeExclude', value: 2 },
      },
      {
        id: 'internalComments',
        label: 'Internal Comments',
        kind: 'single',
        options: INCLUDE_OPTS,
        apply: (r, v) => yesNo((r.counts.comments || 0) > 0, v),
        server: { kind: 'includeExclude', value: 4 },
      },
      {
        id: 'community',
        label: 'Community Comments',
        kind: 'single',
        options: INCLUDE_OPTS,
        apply: (r, v) => yesNo(!!r.hasCommunityComments, v),
        server: { kind: 'includeExclude', value: 5 },
      },
      {
        id: 'bubbleUps',
        label: 'Bubble Ups',
        kind: 'single',
        options: INCLUDE_OPTS,
        unclassifiedOnly: true,
        apply: (r, v) => yesNo(!!r.hasBubbleUp, v),
        server: { kind: 'includeExclude', value: 6 },
      },
      {
        id: 'autoThemis',
        label: 'Auto Themis (below TH)',
        kind: 'single',
        options: INCLUDE_OPTS,
        unclassifiedOnly: true,
        apply: (r, v) => yesNo(!!r.hasAutoThemis, v),
        server: { kind: 'includeExclude', value: 7 },
      },
    ],
  },
]

export const FIELDS = CATEGORIES.flatMap((c) => c.fields.map((f) => ({ ...f, category: c.label })))
export const FIELD_BY_ID = Object.fromEntries(FIELDS.map((f) => [f.id, f]))

/**
 * Tab-scoped filters. Matches legacy IncidentFilters:
 * Bubble Ups / Auto Themis only on Unclassified; Resolved only on Classified.
 */
export const isIncidentFilterOnTab = (field, state) => {
  if (field.classifiedOnly && state !== 'classified') return false
  if (field.unclassifiedOnly && state !== 'unclassified') return false
  return true
}

// Resolve a field's DISPLAY (label + options) — and Challenged Incident type server/apply —
// for the current tab.
// Unclassified → "Themis verdict" (…/No threat detected); Classified/Challenged → "Classification"
// (…/Safe). Challenged → Incident type IRONSCALES/MSFT (not Email/ATO/Teams).
export const resolveFieldForState = (field, state) => {
  if (field && field.id === 'verdict' && state && state !== 'unclassified') {
    return { ...field, label: field.altLabel, options: field.altOptions }
  }
  if (field && field.id === 'type' && state === 'challenged' && field.altOptions) {
    return {
      ...field,
      options: field.altOptions,
      server: field.altServer || field.server,
      apply: field.altApply || field.apply,
    }
  }
  return field
}
export const optionLabel = (field, value) => {
  const o = (field.options || []).find((x) => x.value === value)
  return o ? o.label : value
}

// Chip summary text (spec §4)
export const chipSummary = (field, values) => {
  if (!values || !values.length) return 'any'
  if (field.kind === 'search') return values[0] || 'any'
  if (values.length > 2) return `${values.length} selected`
  return values.map((v) => optionLabel(field, v)).join(', ')
}

// Apply the active filters (+ free-text search + time frame) to a list.
// timeFrame: string from TIME_PRESETS.value ('24h' | '3d' | '7d' | '30d' | 'all' | 'custom' | null)
// customRange: { startDate: Date, endDate: Date } | null (only used when timeFrame === 'custom')
// urlState: optional tab — Challenged Incident type uses IRONSCALES/MSFT apply.
export const applyActiveFilters = (
  list,
  activeFilters,
  search,
  timeFrame,
  customRange,
  urlState
) => {
  let out = list

  // Time frame filter — uses the human-readable `reported` field via parseReportedDate.
  if (timeFrame && timeFrame !== 'all') {
    if (timeFrame === 'custom' && customRange && customRange.startDate) {
      const start = new Date(customRange.startDate)
      start.setHours(0, 0, 0, 0)
      const end = new Date(customRange.endDate || customRange.startDate)
      end.setHours(23, 59, 59, 999)
      out = out.filter((i) => {
        const d = parseReportedDate(i.reported)
        return d ? d >= start && d <= end : true
      })
    } else {
      const start = timeFrameStart(timeFrame)
      if (start) {
        out = out.filter((i) => {
          const d = parseReportedDate(i.reported)
          return d ? d >= start : true
        })
      }
    }
  }

  if (search && search.trim()) {
    const prefixed = extractPrefixedIncidentSearch(search)
    if (prefixed) {
      out = out.filter(
        (i) => String(i.id) === prefixed.reportId && prefixed.reportTypes.includes(i.reportType)
      )
    } else {
      const q = search.toLowerCase()
      const hay = (v) => String(v || '').toLowerCase()
      out = out.filter(
        (i) =>
          hay(i.subject).includes(q) ||
          hay(i.sender && i.sender.name).includes(q) ||
          hay(i.sender && i.sender.email).includes(q) ||
          hay(i.recipient && i.recipient.name).includes(q) ||
          hay(i.recipient && i.recipient.email).includes(q) ||
          hay(i.id).includes(q)
      )
    }
  }
  activeFilters.forEach((af) => {
    const field = resolveFieldForState(FIELD_BY_ID[af.fieldId], urlState)
    if (!field || !af.values || !af.values.length) return
    out = out.filter((row) => field.apply(row, af.values))
  })
  return out
}

// Seed saved filters (one default) to demonstrate the saved-filter flows.
export const SEED_SAVED = [
  {
    id: 'sf-high-risk',
    name: 'High-risk phishing',
    isDefault: false,
    filters: [
      { fieldId: 'verdict', values: ['Phishing'] },
      { fieldId: 'phishingSocAgent', values: ['Include'] },
    ],
  },
  {
    id: 'sf-unassigned-bec',
    name: 'BEC — needs triage',
    isDefault: false,
    filters: [
      { fieldId: 'threatType', values: ['BEC'] },
      { fieldId: 'type', values: ['email'] },
    ],
  },
]
